import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Award, Shield, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const RISK_TONE = {
  "Green":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Yellow":"bg-amber-50 text-amber-800 border-amber-200",
  "Red":"bg-red-50 text-red-700 border-red-200",
};

export default function Placements() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);

  const load = async () => { const {data} = await api.get("/placements"); setRows(data); };
  useEffect(() => { load(); }, []);
  const open = async (id) => { const {data} = await api.get(`/placements/${id}`); setSel(data); };

  const updateCheckin = async (id, patch) => {
    try {
      await api.patch(`/check-ins/${id}`, patch);
      toast.success("Check-in updated");
      if (sel) open(sel.id);
    } catch { toast.error("Failed"); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Placements & Guarantees</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Placements</h1>
        <p className="text-sm text-slate-500 mt-1">Active placements with replacement guarantees and post-placement check-ins.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold">
            <tr>
              <th className="text-left px-4 py-3">Placement</th><th className="text-left px-4 py-3">Service model</th>
              <th className="text-left px-4 py-3">Start</th><th className="text-left px-4 py-3">Fee</th>
              <th className="text-left px-4 py-3">Guarantee</th><th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.id} data-testid={`placement-row-${p.id}`} onClick={()=>open(p.id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3"><div className="flex items-center gap-2"><Award size={14} className="text-purple-600"/><div className="font-semibold text-slate-900 text-xs">Cand {p.candidate_id?.slice(0,8)} · Job {p.job_order_id?.slice(0,8)}</div></div></td>
                <td className="px-4 py-3 text-slate-700">{p.service_model}</td>
                <td className="px-4 py-3 text-slate-600">{p.start_date}</td>
                <td className="px-4 py-3 font-mono">${p.placement_fee?.toLocaleString() || "—"}</td>
                <td className="px-4 py-3 text-xs">{p.guarantee ? `${p.guarantee.days}d · exp ${p.guarantee.expires_at}` : "—"}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-semibold px-2 py-0.5 border border-slate-200 rounded">{p.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-sm text-slate-500">No placements yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {sel && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 flex justify-end" onClick={()=>setSel(null)}>
          <div onClick={e=>e.stopPropagation()} data-testid="placement-drawer" className="w-full max-w-lg bg-white h-full overflow-y-auto p-6 shadow-xl">
            <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Placement details</h3>
            <p className="text-xs text-slate-500 mb-4">{sel.service_model} · started {sel.start_date}</p>

            {sel.guarantee && (
              <div className="mb-5 p-4 rounded-md bg-purple-50 border border-purple-200">
                <div className="flex items-center gap-2 mb-2 text-purple-900 text-xs font-semibold uppercase tracking-widest"><Shield size={12}/> Guarantee</div>
                <div className="text-sm text-slate-800">{sel.guarantee.name}</div>
                <div className="text-xs text-slate-600 mt-1">Active through <span className="font-mono">{sel.guarantee.expires_at}</span> · {sel.guarantee.status}</div>
              </div>
            )}

            <div className="mb-3 flex items-center gap-2 text-slate-500 text-[11px] font-semibold uppercase tracking-widest"><CalendarCheck size={12}/> Post-placement check-ins</div>
            <div className="space-y-2">
              {(sel.check_ins || []).map(c => (
                <div key={c.id} className="border border-slate-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-slate-900">{c.day_offset}-day · {c.target}</div>
                    <div className="text-[10px] text-slate-500">Due {c.due_date}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold px-2 py-0.5 border border-slate-200 rounded">{c.status}</span>
                    {c.risk_level && <span className={`text-[10px] font-semibold px-2 py-0.5 border rounded ${RISK_TONE[c.risk_level]}`}>Risk: {c.risk_level}</span>}
                    <select data-testid={`ci-risk-${c.id}`} defaultValue={c.risk_level||""} onChange={e=>updateCheckin(c.id, {risk_level: e.target.value})} className="text-[10px] border border-slate-200 rounded px-1 h-6 ml-auto">
                      <option value="">Set risk…</option><option>Green</option><option>Yellow</option><option>Red</option>
                    </select>
                    <button data-testid={`ci-done-${c.id}`} onClick={()=>updateCheckin(c.id, {status:"Completed"})} className="text-[10px] px-2 py-0.5 border border-emerald-300 text-emerald-700 rounded font-semibold hover:bg-emerald-50">Mark done</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
